
import { z } from 'zod'
import { mean } from 'lodash'
import { parse } from '@tinyhttp/content-disposition'
import { formatBytes } from '../../functions/format_bytes'
import { isNullish } from '../../functions/nullish'
import { ApiErrorSchema } from '../../modules/mng-dare-import/WebMethods'
import type { Mutex } from 'async-mutex'

export type FileReferenceClientStatus = z.infer<typeof FileReferenceClientStatus>
export const FileReferenceClientStatus = z.enum([
  'InvalidFileReferenceID',
  'TemporaryDowntime',
  'Cancelled',
  'MissingContent',
  'Complete'
])
export type FileReferenceMetadata = z.infer<typeof FileReferenceMetadata>
export const FileReferenceMetadata = z.object({
  FileReferenceID: z.string(),
  Status: FileReferenceClientStatus,
  EstimatedFileSize: z.number().positive().nullable(),
  ContentDownloadUrl: z.string().url().nullable(),
  FileName: z.string().nullable(),
  CreationDate: z.coerce.date().nullable(),
})

export type FileReferenceGroupStatus = z.infer<typeof FileReferenceClientStatus>
export const FileReferenceGroupStatus = z.enum([
  'Open',
  'Closed',
])
export type FileReferenceGroupMetadata = z.infer<typeof FileReferenceGroupMetadata>
export const FileReferenceGroupMetadata = z.object({
  Status: FileReferenceGroupStatus,
  FileCountLimit: z.number().gte(0),
  FileSizeLimit: z.number().gte(0).nullable(),
  RemainingFiles: z.number().gte(0),
  FileReferences: z.string().array(),
})

export type ContentRangeDto = z.infer<typeof ContentRangeDto>
export const ContentRangeDto = z.object({
  // Start: z.bigint().positive(),
  // End: z.bigint().positive(),
  // Length: z.bigint().positive(),
  Start: z.number().gte(0),
  End: z.number().positive(),
  Length: z.number().positive(),
})

export function FormatContentRange(range: ContentRangeDto): string {
  return `bytes ${range.Start}-${range.End}/${range.Length}`;
}

export const UploadPlanType = z.enum([
  'ProxiedChunks',
  'DirectAzureBlobStorage',
]);
export type UploadPlanDto = z.infer<typeof UploadPlanDto>
export const UploadPlanDto = z.intersection(
  z.object({
    Method: UploadPlanType,
    FileSizeLimitBytes: z.number().gte(0),
    Segments: ContentRangeDto.array(),
  }),
  z.discriminatedUnion('Method', [
    z.object({
      Method: z.literal('ProxiedChunks'),
    }),
    z.object({
      Method: z.literal('DirectAzureBlobStorage'),
      AzureBlobStorageSasUploadUrl: z.string().url(),
    })
  ])
);

export type UploadManifestDto = z.infer<typeof UploadManifestDto>
export const UploadManifestDto = z.object({
  FileName: z.string().max(255),
  //FileSize: z.bigint().positive(),
  FileSize: z.number().positive(),
  Crc32Hash: z.string().length(8).regex(/^[0-9A-Fa-f]{8}$/g, "Hash must be hexadecimal formatted"),
})

/** Based on: https://learn.microsoft.com/en-us/dotnet/api/system.iprogress-1.report?view=net-9.0#system-iprogress-1-report(-0) */
export interface IProgress<T> {
  Report(value: T): void;
}

export interface SimpleProgress {
  /** The current progress without any units. This could be bytes or it could be a chunk index. */
  current: number;
  /** The total without any units. This could be bytes or it could be a chunk index. */
  total: number;
  /** The "current" property divided by the "total", which should be in the range of [0, 1.0] */
  ratio: number;
  bytesPerSecond?: number;
}

export interface FileUploadOptions {
  /**
   * Used for entirely cancelling an upload
   */
  abortSignal?: AbortSignal;
  /**
   * Used for tracking upload progress
   */
  externalProgress?: IProgress<SimpleProgress>;
  /**
   * Used for temporarily pausing an upload
   */
  resumableMutex?: Mutex;
}

export class FileReferenceAPI {
  baseURL: URL;
  constructor(base: URL) {
    this.baseURL = base;
  }

  public async Ping(): Promise<Response> {
    return await fetch(`${this.baseURL.href}/api/FileReference/Ping`);
  }

  public async GetFileReferenceMetadata(fileReferenceID: string): Promise<FileReferenceMetadata> {
    const res = await fetch(`${this.baseURL.href}/api/FileReference/${encodeURIComponent(fileReferenceID)}`, {
      method: 'HEAD',
      redirect: 'manual'
    });
    const HttpStatus2FileStatus: Record<number, FileReferenceClientStatus> = {
      404: 'InvalidFileReferenceID',
      409: 'TemporaryDowntime',
      410: 'Cancelled',
      204: 'MissingContent',
      200: 'Complete',
      //302: 'Complete', // Fetch can't see this status for security reasons
    }
    let data: FileReferenceMetadata = {
      FileReferenceID: fileReferenceID,
      // 'Cancelled' will be a fallback status since something clearly went wrong
      Status: HttpStatus2FileStatus[res.status] ?? 'Cancelled',
      EstimatedFileSize: null,
      ContentDownloadUrl: null,
      FileName: null,
      CreationDate: null,
    }

    try {
      data.EstimatedFileSize = Number(res.headers.get('Content-Length') ?? '');
    } catch {}
    try {
      if (res.headers.has('Content-Location')){
        const loc = res.headers.get('Content-Location')!;
        data.ContentDownloadUrl = new URL(loc, window.origin).toString();
      }
    } catch {}
    try {
      // Extract fields from 'Content-Disposition' header
      const headerValue = res.headers.get('Content-Disposition');
      if (headerValue !== null) {
        const content = parse(headerValue);
        if (typeof content.parameters['filename'] === 'string') {
          data.FileName = content.parameters['filename'];
        }
        const parsedDate = FileReferenceMetadata.shape.CreationDate.safeParse(content.parameters['creation-date']);
        if (parsedDate.success) {
          data.CreationDate = parsedDate.data;
        }
      }
    } catch {}
    
    return data;
  }

  public async GetManyFileReferenceMetadata(fileReferenceIDs: string[]): Promise<FileReferenceMetadata[]> {
    const promises = fileReferenceIDs.map(fr => this.GetFileReferenceMetadata(fr));
    const settled = await Promise.allSettled(promises);
    return settled.filter(r => r.status === 'fulfilled').map(r => r.value);
  }

  // #region Uploading
  public async UploadEntireFile(fileReferenceID: string, entireFile: File, options: FileUploadOptions): Promise<void> {
    const { abortSignal, externalProgress, resumableMutex } = options;
    if (entireFile.size === 0) {
      throw new Error('File size cannot be 0');
    }
    const { computeCrc32 } = await import('../../functions/checksum');

    // Compute the Crc32
    console.time('Crc32 Hash');
    const crc32Hash = await computeCrc32(entireFile);
    console.timeEnd('Crc32 Hash');

    // Assemble the manifest
    const manifest: UploadManifestDto = {
      FileName: entireFile.name,
      FileSize: entireFile.size,
      Crc32Hash: crc32Hash,
    }

    // Start the upload session and get back our upload plan
    console.debug(`[FileReferenceAPI] [FRID# ${fileReferenceID}] Starting upload session with manifest:`, manifest);
    const plans = await this.StartUploadSession(fileReferenceID, manifest);
    let failedPlans = 0;

    // Iterate over the available upload plans
    for(let plan of plans) {
      console.debug(`[FileReferenceAPI] [FRID# ${fileReferenceID}] ✅ Upload session started. ${formatBytes(manifest.FileSize)} using '${plan.Method}':`, plan);

      try {

        if (plan.FileSizeLimitBytes >= 0 &&  plan.FileSizeLimitBytes < entireFile.size) {
          throw new Error("File size exceeds limit");
        }

        let ALL_BYTES_SENT = 0;
        let ALL_TOTAL_BYTES = entireFile.size;

        const BANDWIDTH_ROLLING_WINDOW = [0,0,0,0,0];
        let LAST_SENT_TIME_SEC = Date.now() / 1000;
        const UPLOAD_STARTED_SEC = Date.now() / 1000;
        
        if (plan.Method === 'ProxiedChunks') {
          // Follow the upload plan
          for(let i = 0; i < plan.Segments.length; i++) {
            const range = plan.Segments[i];
            /**
             * The 'Content-Range' header is inclusive, but Blob.slice() is exclusive.
             * For example, if a file is `2671719` bytes long, the `range.End`
             * (which is based around Content-Range) will be `2671718`, but the
             * Blob.slice() end index should be `2671719`.
             * This is because there is no byte at the `..19` index, since that is the length
             * of the file and the index starts at 0.
             */
            const chunk = entireFile.slice(range.Start, range.End + 1, 'application/octet-stream');
    
            // Keep track of progress made this chunk
            let CHUNK_BYTES_SENT = 0;
            //let CHUNK_TOTAL_BYTES = chunk.size;
    
            const chunkProgress: IProgress<ProgressEvent> = {
              Report(value) {
                console.debug(`[FileReferenceAPI] [FRID# ${fileReferenceID}] Chunk ${i+1} of ${plan.Segments.length} reported progress: `, value);
                // If the length is computable, we use it to update `currentChunk`
                if (value.lengthComputable) {
    
                  // Compute the transmit rate in bytes-per-second
                  const now = Date.now() / 1000;
                  const transmit_rate_bps = (value.loaded - CHUNK_BYTES_SENT) / (now - LAST_SENT_TIME_SEC);
    
                  // console.log('value.loaded?', value.loaded, 'CHUNK_BYTES_SENT?', CHUNK_BYTES_SENT, 'Now?', now, 'LAST_SENT_TIME_SEC?', LAST_SENT_TIME_SEC);
                  // console.log('Delta bytes?', value.loaded - CHUNK_BYTES_SENT, 'Delta time?', now - LAST_SENT_TIME_SEC);
    
                  // Cycle the rolling window
                  BANDWIDTH_ROLLING_WINDOW.shift();
                  BANDWIDTH_ROLLING_WINDOW.push(transmit_rate_bps);
    
                  // Store how much we've sent so far this chunk
                  CHUNK_BYTES_SENT = value.loaded;
                  // Update the timestamp to measure the next
                  LAST_SENT_TIME_SEC = now;
    
                  // Update external progress only if we have granular updates
                  externalProgress?.Report({
                    current: ALL_BYTES_SENT + value.loaded,
                    total: ALL_TOTAL_BYTES,
                    ratio: (ALL_BYTES_SENT + value.loaded) / ALL_TOTAL_BYTES,
                    // Rolling average of bandwidth
                    bytesPerSecond: mean(BANDWIDTH_ROLLING_WINDOW),
                  });
                }
                else {
                  // Do not make any updates
                  // The only updates we make are the ones made when the Promise completes,
                  // since we can only measure an entire chunk instead of more granular.
                } 
              },
            }

            // Check for a lock, pause execution if we are locked
            if (!isNullish(resumableMutex)) {
              await resumableMutex.waitForUnlock();
            }
    
            console.debug(`[FileReferenceAPI] [FRID# ${fileReferenceID}] Uploading chunk ${i+1} of ${plan.Segments.length}, bytes: ${FormatContentRange(range)} (${formatBytes(chunk.size)})`);
            // TODO: use abort signal here
            await this.UploadChunkTrackProgress(fileReferenceID, range, chunk, chunkProgress);
            ALL_BYTES_SENT += chunk.size;
    
            externalProgress?.Report({
              current: ALL_BYTES_SENT,
              total: ALL_TOTAL_BYTES,
              ratio: ALL_BYTES_SENT / ALL_TOTAL_BYTES,
              // If every value is zero, it's likely because the upload couldn't be computed due to things like `lengthComputable`
              bytesPerSecond: BANDWIDTH_ROLLING_WINDOW.every(v => v === 0) ? undefined : mean(BANDWIDTH_ROLLING_WINDOW),
            });
    
            console.debug(`[FileReferenceAPI] [FRID# ${fileReferenceID}] ✅ Chunk ${i+1} of ${plan.Segments.length} completed`);
          }
        }
        else if (plan.Method === 'DirectAzureBlobStorage') {
          // Upload the file directly to Azure Blob Storage
          // Chunks and splitting is handled behind the scenes for us 😌
          const { BlockBlobClient, AnonymousCredential, newPipeline } = await import('@azure/storage-blob');
          const { ResumablePolicyFactory } = await import('./BlobResumablePolicy');
          // Create a default pipeline with newPipeline
          const pipeline = newPipeline(new AnonymousCredential());
          // Inject customized factory into default pipeline
          if (!isNullish(resumableMutex)) {
            pipeline.factories.unshift(new ResumablePolicyFactory(resumableMutex));
          }
          // Create a BlobClient
          const blobClient = new BlockBlobClient(plan.AzureBlobStorageSasUploadUrl, pipeline);

          const timer = Math.floor(Math.random() * 100);
          console.time(`Direct Blob Upload [${timer}]`);
          const res = await blobClient.uploadData(entireFile, {
            abortSignal: abortSignal,
            // This needs to be small enough that the progress bar will refresh since the Azure SDK developers didn't bother to use the XHR.upload `progress` event.
            maxSingleShotSize: 10_000_000, // 10_000_000 bytes or 10 MB
            concurrency: 2,
            onProgress(progress) {
              // Compute the transmit rate in bytes-per-second
              const now = Date.now() / 1000;
              const transmit_rate_bps = (progress.loadedBytes) / (now - UPLOAD_STARTED_SEC);

              // Update external progress only if we have granular updates
              externalProgress?.Report({
                current: progress.loadedBytes,
                total: ALL_TOTAL_BYTES,
                ratio: progress.loadedBytes / ALL_TOTAL_BYTES,
                // Rolling average of bandwidth
                bytesPerSecond: transmit_rate_bps,
              });
            },
          });
          console.timeEnd(`Direct Blob Upload [${timer}]`);
          
          // Throw error code?
          if (!isNullish(res.errorCode)) {
            throw res.errorCode;
          }
        }

        // Commit the upload session
        await this.CommitUploadSession(fileReferenceID);

        // Don't try anymore plans if nothing went wrong
        break;
      }
      catch (err) {
        // TODO: handle errors a little better so that the user can be told what happened

        console.debug(`[FileReferenceAPI] [FRID# ${fileReferenceID}] ⚠️ Plan using '${plan.Method}' failed. Another plan may be tried instead.:`, plan);

        // If any error, update the count and go to the next plan
        failedPlans += 1;
        continue;
      }

    }

    // If all plans failed, cancel the upload session
    if (failedPlans === plans.length) {
      await this.CancelUploadSession(fileReferenceID);
    }
  }
  
  public async StartUploadSession(fileReferenceID: string, manifest: UploadManifestDto): Promise<UploadPlanDto[]> {
    const res = await fetch(`${this.baseURL.href}/api/FileReference/${encodeURIComponent(fileReferenceID)}/StartUploadSession`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(manifest),
    });
    const data = await res.json();
    const parsed = await UploadPlanDto.array().parseAsync(data);
    return parsed;
  }

  public async UploadChunk(fileReferenceID: string, range: ContentRangeDto, chunk: Blob): Promise<Response> {
    const res = await fetch(`${this.baseURL.href}/api/FileReference/${encodeURIComponent(fileReferenceID)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Range': FormatContentRange(range),
      },
      body: chunk,
    });
    return res;
  }

  public UploadChunkTrackProgress(fileReferenceID: string, range: ContentRangeDto, chunk: Blob, progress: IProgress<ProgressEvent>): Promise<Response> {
    console.time('Chunk Upload');
    console.time('Total Request');
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'text';
      xhr.onreadystatechange = function() {
        if (xhr.readyState !== 4) {
          return;
        }

        const response = new Response(xhr.response, {
          //url: xhr.responseURL,
          status: xhr.status,
          statusText: xhr.statusText,
        });
        
        if (response.ok) {
          console.timeEnd('Total Request');
          resolve(response);
        }
        else {
          reject(response);
        }
      };
      
      xhr.addEventListener('error', (event) => {
        reject(event);
      });

      xhr.upload.addEventListener('progress', (event) => {
        progress.Report(event);
      });

      // When the upload finishes
      xhr.upload.addEventListener('load', (event) => {
        console.timeEnd('Chunk Upload');
      });

      xhr.open('PUT', `${this.baseURL.href}/api/FileReference/${encodeURIComponent(fileReferenceID)}`, true);
      xhr.setRequestHeader('Content-Range', FormatContentRange(range));
      xhr.send(chunk);
    });
  }

  public async CommitUploadSession(fileReferenceID: string): Promise<Response> {
    const res = await fetch(`${this.baseURL.href}/api/FileReference/${encodeURIComponent(fileReferenceID)}/CommitUploadSession`, {
      method: 'POST',
    });
    return res;
  }

  public async CancelUploadSession(fileReferenceID: string): Promise<Response> {
    const res = await fetch(`${this.baseURL.href}/api/FileReference/${encodeURIComponent(fileReferenceID)}/CancelUploadSession`, {
      method: 'POST',
    });
    return res;
  }

  //#endregion
  //#region Groups

  public async GetGroupMetadata(fileReferenceGroupID: string): Promise<FileReferenceGroupMetadata> {
    const res = await fetch(`${this.baseURL.href}/api/FileReference/Group/${encodeURIComponent(fileReferenceGroupID)}`, {
      method: 'GET',
    });
    if (!res.ok) {
      // TODO: throw a particular exception
    }
    const data = await res.json();
    const parsed = await FileReferenceGroupMetadata.parseAsync(data);
    return parsed;
  }

  public async GroupCreateNewFile(fileReferenceGroupID: string): Promise<string> {
    const res = await fetch(`${this.baseURL.href}/api/FileReference/Group/${encodeURIComponent(fileReferenceGroupID)}/GetNewFile`, {
      method: 'POST',
    });
    const data = await res.json();
    const apiError = await ApiErrorSchema.safeParseAsync(data);
    if(apiError.success) {
      throw new Error(apiError.data.Message);
    }
    const parsed = await z.object({ FileReferenceID: z.string() }).parseAsync(data);
    return parsed.FileReferenceID;
  }

  //#endregion

  //#region Test Endpoints (Super Admin permission)

  public async Test_GetNewFile(): Promise<string> {
    const res = await fetch(`${this.baseURL.href}/api/FileReference/Test/GetNewFile`, {
      method: 'POST',
    });
    const data = await res.json();
    const parsed = await z.object({ FileReferenceID: z.string() }).parseAsync(data);
    return parsed.FileReferenceID;
  }

  public async Test_GetNewFileGroup(): Promise<string> {
    const res = await fetch(`${this.baseURL.href}/api/FileReference/Test/GetNewFileGroup`, {
      method: 'POST',
    });
    const data = await res.json();
    const parsed = await z.object({ FileReferenceGroupID: z.string() }).parseAsync(data);
    return parsed.FileReferenceGroupID;
  }

  //#endregion
}
